import React, { ReactElement, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import styles from './Modal.module.scss';

type ModalHeaderProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export const ModalHeader = (props: ModalHeaderProps) => (
    <div {...props} className={`${styles.modalHeader} ${props.className ?? ''}`} />
);

type ModalBodyProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export const ModalBody = (props: ModalBodyProps) => (
    <div {...props} className={`${styles.modalBody} ${props.className ?? ''}`} />
);

type ModalFooterProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export const ModalFooter = (props: ModalFooterProps) => (
    <div {...props} className={`${styles.modalFooter} ${props.className ?? ''}`} />
);

type Props = {
    children: ReactElement | ReactElement[];
} & ReactModal.Props;

const Modal: React.FC<Props> = ({ children, ...props }: Props) => {
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const dragStartRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        if (!target.closest(`.${styles.modalHeader}`)) return;
        if (target.closest('button, input, select, textarea, a, [role="button"]')) return;
        if (e.button !== 0) return;

        dragStartRef.current = {
            x: position.x,
            y: position.y,
            startX: e.clientX,
            startY: e.clientY,
        };
        e.preventDefault();
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!dragStartRef.current) return;
            const dx = e.clientX - dragStartRef.current.startX;
            const dy = e.clientY - dragStartRef.current.startY;
            setPosition({
                x: dragStartRef.current.x + dx,
                y: dragStartRef.current.y + dy,
            });
        };

        const handleMouseUp = () => {
            dragStartRef.current = null;
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    useEffect(() => {
        if (props.isOpen) {
            setPosition({ x: 0, y: 0 });
        }
    }, [props.isOpen]);

    const customStyles = {
        content: {
            ...(props.style?.content ? props.style.content : {}),
            transform: `translate(${position.x}px, ${position.y}px)`,
        },
        overlay: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            zIndex: 200,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            ...(props.style?.overlay ? props.style.overlay : {})
        },
    };

    return (
        <ReactModal
            {...props}
            style={{
                ...(props.style ? props.style : {}),
                ...customStyles,

            }}
            className={`${styles.content} ${props.className}`}
        >
            <div onMouseDown={handleMouseDown} className={styles.dragWrapper}>
                {children}
            </div>
        </ReactModal >
    );
};

export default Modal;
